import { RJSFDocumentRequestProviderField } from "@forms/rjsf/fields/RJSFDocumentRequestProviderField";
import { RJSFLabelField } from "@forms/rjsf/fields/RJSFLabelField";
import { RJSFTitleField } from "@forms/rjsf/fields/RJSFTitleField";
import { RJSFISBNLookupField } from "@forms/rjsf/fields/RJSFISBNLookupField";
import {
  ArrayFieldTemplateWithWrapper,
  FieldTemplateWithWrapper,
  ObjectFieldTemplateWrapperGrid,
} from "@forms/rjsf/RJSFCustomTemplates";
import { RJSFCheckboxWidget } from "@forms/rjsf/widgets/RJSFCheckboxWidget";
import { RJSFReferencedAcqOrder } from "@forms/rjsf/widgets/RJSFReferencedAcqOrder";
import { RJSFReferencedDocument } from "@forms/rjsf/widgets/RJSFReferencedDocument";
import { RJSFReferencedInternalLocation } from "@forms/rjsf/widgets/RJSFReferencedInternalLocation";
import { RJSFReferencedLocation } from "@forms/rjsf/widgets/RJSFReferencedLocation";
import { RJSFReferencedPatron } from "@forms/rjsf/widgets/RJSFReferencedPatron";
import { RJSFReferencedProvider } from "@forms/rjsf/widgets/RJSFReferencedProvider";
import { RJSFTimeWidget } from "@forms/rjsf/widgets/RJSFTimeWidget";
import { RJSFVocabulary } from "@forms/rjsf/widgets/RJSFVocabulary";
import { RJSFVocabularySearch } from "@forms/rjsf/widgets/RJSFVocabularySearch";
import { ISBNLookup } from "@forms/rjsf/ISBNLookup";
import { goBack } from "@history";
import Form from "@rjsf/semantic-ui";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _pick from "lodash/pick";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, Confirm, Container, Divider, Sticky } from "semantic-ui-react";
import { removeEmptyValues } from "./RecordSerializer";
import { ignoreRJSFEnterEvent } from "./utils";
import FormsTabNavigation from "@forms/rjsf/RJSFormsTabNavigation";
import { RJSFDependentVocabulary } from "./widgets/RJSFDependentVocabulary";

const CUSTOM_WIDGETS = {
  CheckboxWidget: RJSFCheckboxWidget,
  timeWidget: RJSFTimeWidget,
  referencedDocument: RJSFReferencedDocument,
  referencedPatron: RJSFReferencedPatron,
  referencedAcqOrder: RJSFReferencedAcqOrder,
  referencedProvider: RJSFReferencedProvider,
  referencedInternalLocation: RJSFReferencedInternalLocation,
  referencedLocation: RJSFReferencedLocation,
  vocabularySearch: RJSFVocabularySearch,
  vocabulary: RJSFVocabulary,
  dependentVocabulary: RJSFDependentVocabulary,
};

const CUSTOM_FIELDS = {
  TitleField: RJSFTitleField,
  documentRequestProviderField: RJSFDocumentRequestProviderField,
  labelField: RJSFLabelField,
  isbnLookupField: RJSFISBNLookupField,
};

const TAB_SECTIONS = [
  { label: "ISBN lookup", fields: [], isISBNTab: true },
  {
    label: "Title proper",
    fields: [
      "title",
      "alternative_titles",
      "edition",
      "document_type",
      "languages",
    ],
  },
  {
    label: "Access points",
    fields: [
      "authors",
      "_tooManyAuthorsCustomField",
      "other_authors",
      "subjects",
      "tags",
      "keywords",
      "identifiers",
    ],
  },
  {
    label: "Publications",
    fields: [
      "imprint",
      "publication_year",
      "publication_info",
      "conference_info",
      "number_of_pages",
    ],
  },
  {
    label: "Abstract",
    fields: [
      "abstract",
      "alternative_abstracts",
      "note",
      "table_of_content",
      "physical_description",
    ],
  },
  {
    label: "Local information",
    fields: [
      "call_number_prefix",
      "call_number",
      "sections",
      "internal_notes",
      "source",
      "curated",
      "restricted",
      "copyrights",
      "licenses",
      "urls",
    ],
  },
];

const SCHEMA_COPY_KEYS = ["definitions", "$schema", "additionalProperties"];

const TITLE_NAME_FIELDS = new Set([
  // Title fields
  "title",
  "alternative_titles",
  "edition",

  // Name fields
  "name",
  "first_name",
  "last_name",
  "full_name",

  // Author fields
  "author",
  "authors",
  "other_authors",

  // Subject and classification fields
  "subjects",
  "keywords",
  "tags",

  // Abstract and description fields
  "abstract",
  "alternative_abstracts",
  "note",
  "internal_notes",
  "physical_description",
  "table_of_content",

  // Publication fields
  "imprint",
  "publication_info",
  "conference_info",

  // Rights and licensing
  "copyrights",
  "licenses",
]);

class CasePreferenceManager {
  constructor() {
    this.preferences = new Map();
    this.previousValues = new Map();
  }

  toSentenceCase(text, fieldKey = "") {
    if (!text || typeof text !== "string" || !text.trim()) return text;

    const firstLetterIndex = text.search(/[a-zA-Z]/);
    if (firstLetterIndex === -1) return text;

    const firstChar = text[firstLetterIndex];
    const firstCharLower = firstChar.toLowerCase();
    const storedPreference = this.preferences.get(fieldKey);

    if (storedPreference?.letter === firstCharLower) {
      const targetChar = storedPreference.wantsLowercase
        ? firstCharLower
        : firstChar.toUpperCase();
      return firstChar !== targetChar
        ? text.substring(0, firstLetterIndex) +
            targetChar +
            text.substring(firstLetterIndex + 1)
        : text;
    }

    const previousValue = this.previousValues.get(fieldKey) || "";
    if (previousValue) {
      const prevFirstLetterIndex = previousValue.search(/[a-zA-Z]/);
      if (prevFirstLetterIndex !== -1) {
        const prevFirstChar = previousValue[prevFirstLetterIndex];
        const prevFirstCharLower = prevFirstChar.toLowerCase();

        if (
          prevFirstCharLower === firstCharLower &&
          prevFirstChar !== firstChar
        ) {
          this.preferences.set(fieldKey, {
            letter: firstCharLower,
            wantsLowercase: firstChar === firstCharLower,
          });
          this.previousValues.set(fieldKey, text);
          return text;
        }
      }
    }

    if (!previousValue && firstChar === firstCharLower) {
      const result =
        text.substring(0, firstLetterIndex) +
        firstChar.toUpperCase() +
        text.substring(firstLetterIndex + 1);
      this.previousValues.set(fieldKey, result);
      return result;
    }

    this.previousValues.set(fieldKey, text);
    return text;
  }

  processFormData(formData, parentKey = "") {
    if (!formData || typeof formData !== "object") return formData;

    const processedData = { ...formData };

    Object.keys(processedData).forEach((key) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const value = processedData[key];

      if (TITLE_NAME_FIELDS.has(key)) {
        if (Array.isArray(value)) {
          processedData[key] = value.map((item, index) => {
            if (typeof item === "string") {
              return this.toSentenceCase(item, `${fullKey}[${index}]`);
            }
            return item && typeof item === "object"
              ? this.processFormData(item, `${fullKey}[${index}]`)
              : item;
          });
        } else if (typeof value === "string") {
          processedData[key] = this.toSentenceCase(value, fullKey);
        } else if (value && typeof value === "object") {
          processedData[key] = this.processFormData(value, fullKey);
        }
      } else if (value && typeof value === "object") {
        processedData[key] = Array.isArray(value)
          ? value.map((item, index) =>
              item && typeof item === "object"
                ? this.processFormData(item, `${fullKey}[${index}]`)
                : item
            )
          : this.processFormData(value, fullKey);
      }
    });

    return processedData;
  }
}

export class RJSForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      errors: {},
      discardConfirmOpen: false,
      activeTab: props.hasMarcData ? 1 : 0,
      allFormData: props.formData || {},
    };
    this.formContainerRef = React.createRef();
    this.caseManager = new CasePreferenceManager();

    this.formUpdateTimeout = null;
    this.pendingSubmit = null;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const propsFormData = nextProps.formData || {};

    if (
      Object.keys(propsFormData).length === 0 ||
      propsFormData === prevState.allFormData ||
      propsFormData === prevState.prevPropsFormData
    ) {
      return { prevPropsFormData: propsFormData };
    }

    const stateDataLength = Object.keys(prevState.allFormData || {}).length;
    const propsDataLength = Object.keys(propsFormData).length;

    return stateDataLength > propsDataLength
      ? { prevPropsFormData: propsFormData }
      : { allFormData: propsFormData, prevPropsFormData: propsFormData };
  }

  componentDidMount() {
    ignoreRJSFEnterEvent(document);
  }

  componentWillUnmount() {
    if (this.formUpdateTimeout) {
      clearTimeout(this.formUpdateTimeout);
    }

    if (this.pendingSubmit && this.pendingSubmit.cancel) {
      this.pendingSubmit.cancel();
    }

    this.setState = () => {};
  }
  setActiveTab = (index) => {
    this.setState({ activeTab: index });
  };

  handleISBNDataFetched = (isbnData) => {
    const processedIsbnData = this.caseManager.processFormData(isbnData);
    this.setState((prevState) => ({
      allFormData: { ...prevState.allFormData, ...processedIsbnData },
      activeTab: 1,
      prevPropsFormData: this.props.formData,
    }));
  };

  getDocumentPid = () => {
    if (this.props.documentPid) return this.props.documentPid;

    const pidMatch = window.location.pathname.match(
      /\/documents\/([^/]+)(?:\/|$)/
    );
    if (pidMatch && pidMatch[1] !== "new") return pidMatch[1];

    return this.state.allFormData?.pid || null;
  };

  getCurrentCover = () => {
    const { allFormData } = this.state;
    const coverUrls = allFormData?.cover_urls;
    return coverUrls?.large || coverUrls?.medium || coverUrls?.small || null;
  };

  getCurrentTabData = () => {
    const { schema, uiSchema, isDocumentForm } = this.props;
    const { activeTab, allFormData } = this.state;

    if (!isDocumentForm) {
      return { schema, uiSchema, formData: allFormData };
    }

    const currentSection = TAB_SECTIONS[activeTab];
    if (!currentSection?.fields || currentSection.isISBNTab) {
      return {
        schema: { type: "object", properties: {}, required: [] },
        uiSchema: {},
        formData: {},
      };
    }

    const tabSchema = {
      type: schema.type || "object",
      properties: _pick(schema.properties, currentSection.fields),
      required:
        schema.required?.filter((field) =>
          currentSection.fields.includes(field)
        ) || [],
    };

    SCHEMA_COPY_KEYS.forEach((key) => {
      if (schema[key] !== undefined) tabSchema[key] = schema[key];
    });

    // Special handling for "Local information" tab to put call number fields in one row
    let customGrid;
    if (currentSection.label === "Local information") {
      customGrid = [
        {
          sections: 5,
          call_number_prefix: 5,
          call_number: 6,
        },
        { internal_notes: 16 },
        { source: 8, curated: 4, restricted: 4 },
        { copyrights: 8, licenses: 8 },
        { urls: 16 },
      ];
    } else {
      // Default behavior for all other tabs - each field gets full width
      customGrid = currentSection.fields.map((field) => ({ [field]: 16 }));
    }

    const tabUiSchema = {
      ...Object.fromEntries(
        Object.entries(uiSchema).filter(
          ([key]) =>
            currentSection.fields.includes(key) ||
            key.startsWith("ui:") ||
            key === "custom:root"
        )
      ),
      "custom:grid": customGrid,
    };

    if (uiSchema["ui:order"]) {
      tabUiSchema["ui:order"] = uiSchema["ui:order"].filter((field) =>
        currentSection.fields.includes(field)
      );
    }

    return {
      schema: tabSchema,
      uiSchema: tabUiSchema,
      formData: _pick(allFormData, currentSection.fields),
    };
  };

  onFormChange = ({ formData }) => {
    const cost = parseFloat(formData.cost) || 0;
    const copiesReceived = parseInt(formData.copies_received) || 0;
    const prevCost = parseFloat(this.state.allFormData?.cost) || 0;
    const prevCopies = parseInt(this.state.allFormData?.copies_received) || 0;
    const costChanged = cost !== prevCost;
    const copiesChanged = copiesReceived !== prevCopies;

    let totalCost = formData.total_cost;

    if (costChanged || copiesChanged) {
      totalCost = Math.round(cost * copiesReceived * 100) / 100;
    }

    const formDataWithTotal = {
      ...formData,
      total_cost: totalCost,
    };

    const processedFormData =
      this.caseManager.processFormData(formDataWithTotal);
    processedFormData.total_cost = totalCost;

    if (!this.props.isDocumentForm) {
      this.setState({
        allFormData: processedFormData,
        errors: {},
      });

      if (this.props.onChange) {
        this.props.onChange({ formData: processedFormData });
      }
      return;
    }

    const currentSection = TAB_SECTIONS[this.state.activeTab];
    if (currentSection && !currentSection.isISBNTab) {
      this.setState((prevState) => ({
        allFormData: { ...prevState.allFormData, ...processedFormData },
        errors: {},
      }));
    }
  };

  createGenericError = (message) => ({ Error: { __errors: [message] } });

  onError = (error, formData) => {
    if (error?.skipFormError) {
      this.setState({ isLoading: false });
      return;
    }

    const errors = _get(error, "response.data.errors", []);
    let extraErrors = {};

    if (!_isEmpty(errors)) {
      for (const fieldError of errors) {
        if (_isEmpty(fieldError.field)) {
          extraErrors = this.createGenericError(fieldError.message);
          break;
        }
        extraErrors[fieldError.field] = { __errors: [fieldError.message] };
      }
    }

    this.setState({
      isLoading: false,
      errors: extraErrors,
    });
  };

  validateAllTabs = () => {
    const { allFormData } = this.state;
    const { schema, isDocumentForm } = this.props;

    if (!schema.required) return true;

    const missingFields = schema.required.filter((field) => {
      const value = allFormData[field];
      return (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "string" && !value.trim())
      );
    });

    if (missingFields.length === 0) return true;

    if (isDocumentForm) {
      const tabIndex = TAB_SECTIONS.findIndex(
        (section) =>
          !section.isISBNTab &&
          section.fields.some((field) => missingFields.includes(field))
      );
      if (tabIndex !== -1) {
        this.setState({ activeTab: tabIndex });
      }
    }

    this.setState({
      errors: this.createGenericError(
        `Please fill in the required fields: ${missingFields.join(", ")}`
      ),
    });
    return false;
  };

  onSubmit = async ({ formData }) => {
    const {
      sendSuccessNotification,
      submitAction,
      successCallback,
      successMessage,
    } = this.props;

    const completeFormData = this.caseManager.processFormData({
      ...this.state.allFormData,
      ...formData,
    });

    if (!this.validateAllTabs()) return;

    const cleanedFormData = removeEmptyValues(completeFormData);
    this.setState({ isLoading: true });

    try {
      this.pendingSubmit = submitAction(cleanedFormData);
      const response = await this.pendingSubmit;

      sendSuccessNotification("Success!", successMessage);
      if (successCallback) successCallback(response);
      this.setState({ isLoading: false });
      window.scrollTo(0, 0);
    } catch (error) {
      if (error?.message === "Validation failed" || error?.skipFormError) {
        this.setState({ isLoading: false });
        return;
      }

      this.onError(error, cleanedFormData);
    } finally {
      this.pendingSubmit = null;
    }
  };

  handleDiscardConfirm = () => {
    this.setState({ discardConfirmOpen: false });
    goBack();
  };

  render() {
    const { isDocumentForm } = this.props;
    const { discardConfirmOpen, errors, isLoading, activeTab } = this.state;

    const {
      schema: currentTabSchema,
      uiSchema: currentTabUiSchema,
      formData: currentTabFormData,
    } = this.getCurrentTabData();

    const navSections = isDocumentForm
      ? TAB_SECTIONS.map((section, index) => ({
          label: section.label,
          targetId: `tab-${index}`,
          onClick: () => this.setActiveTab(index),
        }))
      : [];

    const isISBNLookupTab =
      isDocumentForm && TAB_SECTIONS[activeTab]?.isISBNTab;

    return (
      <div ref={this.formContainerRef}>
        {isDocumentForm && (
          <Container className="forms-tab-container">
            <Sticky
              className="solid-background"
              context={this.formContainerRef}
            >
              <FormsTabNavigation
                sections={navSections}
                activeTab={activeTab}
                setActiveTab={this.setActiveTab}
              />
            </Sticky>
          </Container>
        )}

        <Container>
          <div style={{ minHeight: "400px", padding: "20px 0" }}>
            {isISBNLookupTab ? (
              <div style={{ padding: "20px 0" }}>
                <ISBNLookup
                  onDataFetched={this.handleISBNDataFetched}
                  onFileSelected={this.props.onCoverFileSelected}
                  isVisible
                  documentPid={this.getDocumentPid()}
                  currentCover={this.getCurrentCover()}
                  showTabs={false}
                />
              </div>
            ) : (
              <Form
                key={isDocumentForm ? `tab-${activeTab}` : "form"}
                schema={currentTabSchema}
                uiSchema={currentTabUiSchema}
                formData={currentTabFormData}
                formContext={currentTabFormData}
                widgets={CUSTOM_WIDGETS}
                fields={CUSTOM_FIELDS}
                transformErrors={(errors) => {
                  if (errors.length) window.scrollTo(0, 0);
                  return errors;
                }}
                extraErrors={errors}
                ObjectFieldTemplate={ObjectFieldTemplateWrapperGrid}
                FieldTemplate={FieldTemplateWithWrapper}
                ArrayFieldTemplate={ArrayFieldTemplateWithWrapper}
                disabled={false}
                onChange={this.onFormChange}
                onSubmit={this.onSubmit}
              >
                <Container textAlign="right">
                  <Divider hidden />
                  <Confirm
                    content="Are you sure you want to discard any change?"
                    open={discardConfirmOpen}
                    cancelButton="No, keep editing"
                    confirmButton="Yes, discard changes"
                    onCancel={() =>
                      this.setState({ discardConfirmOpen: false })
                    }
                    onConfirm={this.handleDiscardConfirm}
                  />
                  <Button
                    type="button"
                    content="Discard"
                    disabled={isLoading}
                    onClick={() => this.setState({ discardConfirmOpen: true })}
                  />
                  <Button
                    primary
                    icon="check"
                    labelPosition="left"
                    name="submit"
                    type="submit"
                    content="Submit"
                    loading={isLoading}
                    disabled={isLoading}
                  />
                </Container>
              </Form>
            )}
          </div>
        </Container>
      </div>
    );
  }
}

RJSForm.propTypes = {
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  submitAction: PropTypes.func.isRequired,
  successCallback: PropTypes.func,
  sendSuccessNotification: PropTypes.func.isRequired,
  successMessage: PropTypes.string.isRequired,
  isDocumentForm: PropTypes.bool,
  documentPid: PropTypes.string,
  onCoverFileSelected: PropTypes.func,
  hasMarcData: PropTypes.bool,
};

RJSForm.defaultProps = {
  formData: {},
  onChange: null,
  successCallback: null,
  isDocumentForm: false,
  documentPid: null,
  onCoverFileSelected: null,
  hasMarcData: false,
};
